import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const SHOP_ITEMS = [
  {
    key: 'celebrity_judge',
    name: 'Celebrity Judge',
    price: 250,
    blurb: 'Adds a celebrity badge treatment to your verdict card.',
  },
  {
    key: 'theme_pack',
    name: 'Theme Pack',
    price: 400,
    blurb: 'Unlocks alternate trial styling themes.',
  },
  {
    key: 'extra_summons',
    name: 'Extra Summons',
    price: 600,
    blurb: 'Increases your summon cap for faster jury fill.',
  },
  {
    key: 'streak_freeze',
    name: 'Streak Freeze',
    price: 250,
    blurb: 'Auto-saves your streak one time if you miss a day. Hold up to 2.',
  },
];

export default function CoinShop() {
  const [userId, setUserId] = useState(null);
  const [coins, setCoins] = useState(0);
  const [owned, setOwned] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState(null);
  const [error, setError] = useState('');
  const [streakFreezeCount, setStreakFreezeCount] = useState(0);

  const loadShopState = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: auth } = await supabase.auth.getUser();
      const authUserId = auth?.user?.id;
      if (!authUserId) {
        setError('Sign in to use Coin Shop.');
        setLoading(false);
        return;
      }

      const { data: appUser, error: appUserErr } = await supabase
        .from('users')
        .select('id, coins')
        .eq('auth_user_id', authUserId)
        .maybeSingle();
      if (appUserErr) throw appUserErr;
      if (!appUser?.id) {
        setError('Could not load your user profile.');
        setLoading(false);
        return;
      }

      setUserId(appUser.id);
      setCoins(Number(appUser.coins || 0));

      const { data: purchases, error: purchaseErr } = await supabase
        .from('user_coin_shop_purchases')
        .select('item_key')
        .eq('user_id', appUser.id);
      if (purchaseErr) throw purchaseErr;
      setOwned(new Set((purchases || []).map((row) => row.item_key)));

      const { data: freezeRow, error: freezeErr } = await supabase
        .from('user_streak_freezes')
        .select('freeze_count')
        .eq('user_id', appUser.id)
        .maybeSingle();
      if (freezeErr && freezeErr.code !== 'PGRST116') throw freezeErr;
      setStreakFreezeCount(Number(freezeRow?.freeze_count || 0));
    } catch (e) {
      console.error('[coin-shop] load_failed', e);
      setError('Could not load Coin Shop right now.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShopState();
  }, [loadShopState]);

  const purchaseItem = useCallback(async (item) => {
    if (!userId || pendingKey) return;
    if (owned.has(item.key)) return;

    setPendingKey(item.key);
    setError('');
    try {
      if (item.key === 'streak_freeze') {
        const { data, error: rpcErr } = await supabase.rpc('purchase_streak_freeze', {
          p_cost: item.price,
        });
        if (rpcErr) throw rpcErr;
        if (!data?.ok) {
          if (data?.error === 'insufficient_coins') {
            setError('Not enough coins. Vote daily and keep your streak to earn more.');
          } else if (data?.error === 'freeze_cap_reached') {
            setError('You already have the max of 2 streak freezes.');
          } else {
            setError('Purchase failed. Please try again.');
          }
          if (Number.isFinite(Number(data?.new_balance))) setCoins(Number(data.new_balance));
          if (Number.isFinite(Number(data?.freeze_count))) setStreakFreezeCount(Number(data.freeze_count));
          return;
        }
        if (Number.isFinite(Number(data?.new_balance))) setCoins(Number(data.new_balance));
        if (Number.isFinite(Number(data?.freeze_count))) setStreakFreezeCount(Number(data.freeze_count));
        return;
      }

      const { data, error: rpcErr } = await supabase.rpc('purchase_coin_shop_item', {
        p_item_key: item.key,
        p_cost: item.price,
        p_metadata: { source: 'coin_shop_ui' },
      });
      if (rpcErr) throw rpcErr;
      if (!data?.ok) {
        if (data?.error === 'insufficient_coins') {
          setError('Not enough coins. Vote daily and keep your streak to earn more.');
        } else {
          setError('Purchase failed. Please try again.');
        }
        if (Number.isFinite(Number(data?.new_balance))) {
          setCoins(Number(data.new_balance));
        }
        return;
      }

      if (Number.isFinite(Number(data?.new_balance))) {
        setCoins(Number(data.new_balance));
      }
      setOwned((prev) => new Set([...prev, item.key]));
    } catch (e) {
      console.error('[coin-shop] purchase_failed', e);
      setError('Purchase failed. Please try again.');
    } finally {
      setPendingKey(null);
    }
  }, [owned, pendingKey, userId]);

  const balanceLabel = useMemo(() => `${coins.toLocaleString()} coins`, [coins]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="text-sm text-slate-300 hover:text-slate-100">← Back</Link>
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold bg-cyan-500/15 border border-cyan-400/40 px-3 py-1 rounded-full">🧊 {streakFreezeCount}/2 freezes</div>
            <div className="text-sm font-semibold bg-yellow-500/15 border border-yellow-400/40 px-3 py-1 rounded-full">💰 {balanceLabel}</div>
          </div>
        </div>

        <div>
          <h1 className="text-2xl font-black">Coin Shop</h1>
          <p className="text-slate-300 text-sm">Spend coins on utility perks and cosmetics. No payments required in beta.</p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</div>
        )}

        {loading ? (
          <div className="text-slate-400 text-sm">Loading shop...</div>
        ) : (
          <div className="space-y-3">
            {SHOP_ITEMS.map((item) => {
              const isFreeze = item.key === 'streak_freeze';
              const isOwned = isFreeze ? false : owned.has(item.key);
              const canAfford = coins >= item.price;
              const isPending = pendingKey === item.key;
              const freezeCapReached = isFreeze && streakFreezeCount >= 2;
              return (
                <div key={item.key} className="rounded-xl border border-slate-700 bg-slate-900/80 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-bold text-slate-50">{item.name}</div>
                      <div className="text-sm text-slate-300">{item.blurb}</div>
                    </div>
                    <div className="text-sm font-semibold text-yellow-300">{item.price} 💰</div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-xs text-slate-400">
                      {isFreeze
                        ? freezeCapReached
                          ? 'Freeze inventory full'
                          : canAfford
                            ? `Ready to buy • holding ${streakFreezeCount}/2`
                            : 'Not enough coins'
                        : isOwned
                          ? 'Owned'
                          : canAfford
                            ? 'Ready to buy'
                            : 'Not enough coins'}
                    </div>
                    <button
                      disabled={(isOwned && !isFreeze) || !canAfford || isPending || freezeCapReached}
                      onClick={() => purchaseItem(item)}
                      className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                        (isOwned && !isFreeze) || freezeCapReached
                          ? 'bg-slate-700 text-slate-300 cursor-not-allowed'
                          : !canAfford
                            ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                            : 'bg-purple-600 hover:bg-purple-500 text-white'
                      }`}
                    >
                      {isFreeze
                        ? freezeCapReached
                          ? 'Maxed'
                          : isPending
                            ? 'Buying...'
                            : 'Buy'
                        : isOwned
                          ? 'Owned'
                          : isPending
                            ? 'Buying...'
                            : 'Buy'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
